import React from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Chip,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider,
  Alert,
} from '@mui/material';
import {
  Wifi as ConnectionIcon,
  Storage as DatabaseIcon,
  Update as RealtimeIcon,
  Security as SecurityIcon,
  Info as InfoIcon,
} from '@mui/icons-material';
import { useQuery } from 'react-query';
import { useSupabase } from '../contexts/SupabaseContext';

const SystemStatus: React.FC = () => {
  const { supabase, isConnected } = useSupabase();

  // Fetch system statistics
  const { data: stats, isLoading } = useQuery('system-stats', async () => {
    const [
      templatesResult,
      devicesResult,
      responsesResult,
    ] = await Promise.all([
      supabase.from('form_templates').select('id', { count: 'exact' }),
      supabase.from('devices').select('id', { count: 'exact' }),
      supabase.from('form_responses').select('id', { count: 'exact' }),
    ]);

    return {
      templates: templatesResult.count || 0,
      devices: devicesResult.count || 0,
      responses: responsesResult.count || 0,
    };
  });

  const systemInfo: Array<{
    title: string;
    status: string;
    icon: React.ReactElement;
    color: 'success' | 'error' | 'warning' | 'info';
    description: string;
  }> = [
    {
      title: 'Database Connection',
      status: isConnected ? 'Connected' : 'Disconnected',
      icon: <DatabaseIcon />,
      color: isConnected ? 'success' : 'error',
      description: isConnected 
        ? 'Successfully connected to Supabase database'
        : 'Unable to connect to database',
    },
    {
      title: 'Real-time Updates',
      status: 'Active',
      icon: <RealtimeIcon />,
      color: 'success',
      description: 'Real-time subscriptions are working',
    },
    {
      title: 'API Endpoints',
      status: 'Available',
      icon: <ConnectionIcon />,
      color: 'success',
      description: 'All API endpoints are responding',
    },
    {
      title: 'Security',
      status: 'Enabled',
      icon: <SecurityIcon />,
      color: 'success',
      description: 'Row Level Security (RLS) is active',
    },
  ];

  const appInfo = [
    { label: 'Application Name', value: 'Informed Consent Management System' },
    { label: 'Version', value: '1.0.0' },
    { label: 'Environment', value: process.env.NODE_ENV === 'production' ? 'Production' : 'Development' },
    { label: 'Supabase URL', value: process.env.REACT_APP_SUPABASE_URL || 'Not configured' },
    { label: 'Last Updated', value: new Date().toLocaleDateString() },
  ];

  return (
    <Box>
      <Typography variant="h4" component="h1" gutterBottom>
        System Status
      </Typography>

      {/* System Status Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {systemInfo.map((item) => (
          <Grid item xs={12} sm={6} md={3} key={item.title}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Box
                    sx={{
                      backgroundColor: item.color === 'success' ? '#7FB069' : '#E74C3C',
                      borderRadius: '50%',
                      p: 1,
                      mr: 2,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    {React.cloneElement(item.icon, { sx: { color: 'white' } })}
                  </Box>
                  <Typography variant="h6" component="div">
                    {item.title}
                  </Typography>
                </Box>
                <Chip
                  label={item.status}
                  color={item.color}
                  size="small"
                  sx={{ mb: 1 }}
                />
                <Typography variant="body2" color="text.secondary">
                  {item.description}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Database Statistics */}
      <Card sx={{ mb: 4 }}>
        <CardContent>
          <Typography variant="h5" gutterBottom>
            Database Statistics
          </Typography>
          {isLoading ? (
            <Typography>Loading statistics...</Typography>
          ) : (
            <Grid container spacing={2}>
              <Grid item xs={4}>
                <Typography variant="h6" color="primary">
                  {stats?.templates || 0}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Form Templates
                </Typography>
              </Grid>
              <Grid item xs={4}>
                <Typography variant="h6" color="primary">
                  {stats?.devices || 0}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Devices
                </Typography>
              </Grid>
              <Grid item xs={4}>
                <Typography variant="h6" color="primary">
                  {stats?.responses || 0}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Form Responses
                </Typography>
              </Grid>
            </Grid>
          )}
        </CardContent>
      </Card>

      {/* Application Information */}
      <Card>
        <CardContent>
          <Typography variant="h5" gutterBottom>
            Application Information
          </Typography>
          <List>
            {appInfo.map((item, index) => (
              <React.Fragment key={item.label}>
                <ListItem>
                  <ListItemIcon>
                    <InfoIcon color="primary" />
                  </ListItemIcon>
                  <ListItemText
                    primary={item.label}
                    secondary={item.value}
                  />
                </ListItem>
                {index < appInfo.length - 1 && <Divider />}
              </React.Fragment>
            ))}
          </List>
        </CardContent>
      </Card>

      {/* System Health Alert */}
      {!isConnected && (
        <Alert severity="error" sx={{ mt: 2 }}>
          <Typography variant="body2">
            Database connection is not available. Please check your configuration.
          </Typography>
        </Alert>
      )}
    </Box>
  );
};

export default SystemStatus;
